import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Grid, CircularProgress,
  Card, CardContent, Alert, Chip, IconButton, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  QrCode as QrCodeIcon,
  Check as ConnectedIcon,
  Close as DisconnectedIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WhatsAppIntegration() {
  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrCode, setQrCode] = useState(null);
  const [status, setStatus] = useState('disconnected');
  const [statusLoading, setStatusLoading] = useState(false);
  const [lastStatusCheckedAt, setLastStatusCheckedAt] = useState(null);
  const [connectingSince, setConnectingSince] = useState(null);
  const [messages, setMessages] = useState([]);
  const [openSend, setOpenSend] = useState(false);
  const [openDisconnectConfirm, setOpenDisconnectConfirm] = useState(false);
  const [sendData, setSendData] = useState({ phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const pollingRef = useRef(false);

  useEffect(() => {
    fetchInstance();
    fetchMessages();
  }, []);

  useEffect(() => {
    let interval;
    if (status === 'connecting' && qrCode) {
      interval = setInterval(checkStatus, 5000);
    }
    return () => clearInterval(interval);
  }, [status, qrCode]);

  const fetchInstance = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/instance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.has_instance) {
        setInstance(response.data.instance);
        setStatus(response.data.instance.status);
        return response.data.instance;
      }
      setInstance(null);
      return null;
    } catch (error) {
      console.error('Erro ao buscar instância');
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/messages?per_page=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.data || []);
    } catch (error) {
      console.error('Erro ao buscar mensagens');
    }
  };

  const createInstance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/whatsapp/instance`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstance(response.data.instance);
      toast.success('Instância criada! Agora conecte o WhatsApp.');
      return response.data.instance;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao criar instância');
      return null;
    }
  };

  const getQrCode = async ({ retryOnMissing = true } = {}) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/qrcode`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrCode(response.data.qr_code);
      setStatus(response.data.status || 'connecting');
      setConnectingSince(new Date());
    } catch (error) {
      const statusCode = error.response?.status;
      const message = error.response?.data?.message || '';
      if (retryOnMissing && (statusCode === 404 || message.includes('Instância não encontrada'))) {
        const created = await createInstance();
        if (created) {
          return getQrCode({ retryOnMissing: false });
        }
      }
      toast.error(message || 'Erro ao obter QR Code');
    }
  };

  const checkStatus = async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setStatusLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const nextStatus = response.data.status;
      if (response.data.qr_code) {
        setQrCode(response.data.qr_code);
      }
      setStatus(nextStatus);
      setLastStatusCheckedAt(new Date());
      
      if (nextStatus === 'connected') {
        const latestInstance = await fetchInstance({ silent: true });
        if (latestInstance?.phone_number) {
          setQrCode(null);
          setConnectingSince(null);
          toast.success('WhatsApp conectado!');
        }
      } else if (nextStatus === 'disconnected') {
        setQrCode(null);
        setConnectingSince(null);
      } else if (nextStatus === 'connecting' && connectingSince) {
        const elapsedMs = Date.now() - new Date(connectingSince).getTime();
        if (elapsedMs > 60000) {
          await getQrCode({ retryOnMissing: false });
        }
      }
    } catch (error) {
      console.error('Erro ao verificar status');
    } finally {
      setStatusLoading(false);
      pollingRef.current = false;
    }
  };

  const disconnect = async () => {
    try {
      setDisconnecting(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/whatsapp/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus('disconnected');
      setQrCode(null);
      setConnectingSince(null);
      setOpenDisconnectConfirm(false);
      toast.success('WhatsApp desconectado');
    } catch (error) {
      toast.error('Erro ao desconectar');
    } finally {
      setDisconnecting(false);
    }
  };

  const sendMessage = async () => {
    if (!sendData.phone || !sendData.message) {
      toast.error('Preencha telefone e mensagem');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/whatsapp/send`, sendData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mensagem enviada!');
      setOpenSend(false);
      setSendData({ phone: '', message: '' });
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  const getStatusChip = () => {
    switch (status) {
      case 'connected':
        return <Chip icon={<ConnectedIcon />} label="Conectado" color="success" />;
      case 'connecting':
        return <Chip icon={<CircularProgress size={16} />} label="Conectando..." color="warning" />;
      default:
        return <Chip icon={<DisconnectedIcon />} label="Desconectado" color="default" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">
          <WhatsAppIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#25D366' }} />
          Integração WhatsApp
        </Typography>
        {status === 'connected' && (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setOpenSend(true)}
          >
            Enviar Mensagem
          </Button>
        )}
      </Box>

      {/* Status Card */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Status da Conexão</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={checkStatus}
                    disabled={statusLoading}
                  >
                    {statusLoading ? 'Atualizando...' : 'Atualizar status'}
                  </Button>
                  {getStatusChip()}
                </Box>
              </Box>
              {lastStatusCheckedAt && (
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                  Última verificação: {lastStatusCheckedAt.toLocaleTimeString('pt-BR')}
                </Typography>
              )}

              {!instance ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography color="textSecondary" sx={{ mb: 2 }}>
                    Nenhuma instância configurada
                  </Typography>
                  <Button variant="contained" onClick={createInstance}>
                    Criar Instância
                  </Button>
                </Box>
              ) : status === 'disconnected' ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography color="textSecondary" sx={{ mb: 2 }}>
                    Escaneie o QR Code para conectar
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<QrCodeIcon />}
                    onClick={getQrCode}
                  >
                    Gerar QR Code
                  </Button>
                </Box>
              ) : status === 'connecting' && qrCode ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography sx={{ mb: 2 }}>Escaneie com seu WhatsApp:</Typography>
                  <img
                    src={`data:image/png;base64,${qrCode}`}
                    alt="QR Code"
                    style={{ maxWidth: 256, border: '1px solid #ddd', borderRadius: 8 }}
                  />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Aguardando conexão...
                  </Typography>
                </Box>
              ) : status === 'connected' ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    WhatsApp conectado e pronto para enviar mensagens!
                  </Alert>
                  {instance.phone_number && (
                    <Typography>Número: <strong>{instance.phone_number}</strong></Typography>
                  )}
                  <Box sx={{ mt: 2 }}>
                    <Button color="error" onClick={() => setOpenDisconnectConfirm(true)}>
                      Desconectar
                    </Button>
                  </Box>
                </Box>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>O que você pode fazer</Typography>
              <Box sx={{ '& > *': { mb: 1 } }}>
                <Chip icon={<ReceiptIcon />} label="Enviar comprovante de venda" sx={{ mr: 1 }} />
                <Chip icon={<SendIcon />} label="Enviar mensagens" sx={{ mr: 1 }} />
                <Chip label="Lembretes de cobrança" />
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                Configure a Evolution API para usar esta integração.
                Você precisa ter o servidor Evolution API rodando.
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Histórico de Mensagens */}
      <Paper sx={{ mt: 3 }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Histórico de Mensagens</Typography>
          <IconButton onClick={fetchMessages}>
            <RefreshIcon />
          </IconButton>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Mensagem</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Nenhuma mensagem enviada
                  </TableCell>
                </TableRow>
              ) : (
                messages.map((msg) => (
                  <TableRow key={msg.id}>
                    <TableCell>
                      {new Date(msg.created_at).toLocaleString('pt-BR')}
                    </TableCell>
                    <TableCell>{msg.phone_number}</TableCell>
                    <TableCell>
                      <Chip
                        label={msg.message_type === 'sale' ? 'Venda' : msg.message_type === 'reminder' ? 'Lembrete' : 'Mensagem'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {msg.message_content.substring(0, 50)}...
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={msg.status === 'sent' ? 'Enviada' : msg.status === 'delivered' ? 'Entregue' : msg.status === 'failed' ? 'Falhou' : 'Pendente'}
                        color={msg.status === 'sent' || msg.status === 'delivered' ? 'success' : msg.status === 'failed' ? 'error' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog Enviar Mensagem */}
      <Dialog open={openSend} onClose={() => setOpenSend(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar Mensagem</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Telefone (com DDD)"
                placeholder="11999999999"
                value={sendData.phone}
                onChange={(e) => setSendData({ ...sendData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Mensagem"
                value={sendData.message}
                onChange={(e) => setSendData({ ...sendData, message: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSend(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={sendMessage}
            disabled={sending}
            startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sending ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDisconnectConfirm}
        onClose={() => !disconnecting && setOpenDisconnectConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Desconectar WhatsApp</DialogTitle>
        <DialogContent>
          <Typography>
            Deseja desconectar o WhatsApp?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDisconnectConfirm(false)}
            disabled={disconnecting}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={disconnect}
            disabled={disconnecting}
          >
            {disconnecting ? 'Desconectando...' : 'Desconectar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
